/**
 * Uniform loading / error / empty body of the extranet cards: a `Spinner`
 * while loading, an error `Alert` with the message, the fiches' empty text
 * « aucune donnée disponible » — otherwise the card content.
 *
 * Chrome helper of the shared cards — not registered.
 */

import type { ReactNode } from 'react';

import { Text } from '@components/base/display';
import { Alert, Spinner } from '@components/base/feedback';

import { EMPTY_STATE_TEXT } from './cardText';

export interface CardBodyStateProps {
  loading?: boolean;
  error?: string | null;
  /** No data to show (rendered after loading / error). */
  empty?: boolean;
  /** Overrides {@link EMPTY_STATE_TEXT}. */
  emptyMessage?: string;
  children: ReactNode;
}

export function CardBodyState({
  loading = false,
  error = null,
  empty = false,
  emptyMessage = EMPTY_STATE_TEXT,
  children,
}: Readonly<CardBodyStateProps>) {
  if (loading) {
    return (
      <div className="flex min-h-24 items-center justify-center p-5" data-testid="card-loading">
        <Spinner size="md" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-5">
        <Alert variant="error">{error}</Alert>
      </div>
    );
  }
  if (empty) {
    return (
      <div className="p-5">
        <Text size="sm" color="muted">
          {emptyMessage}
        </Text>
      </div>
    );
  }
  return <>{children}</>;
}
